import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { CartDrawer } from "./CartDrawer";

export function ShopHeader() {
  const { count, openCart } = useCart();
  const { isStaff } = useAuth();
  const [open, setOpen] = useState(false);
  const [whatsapp, setWhatsapp] = useState("+228 78 89 19 45");
  const location = useLocation();
  const isMinistry = location.pathname.startsWith("/ministere");
  const isShop = ["/boutique", "/panier", "/commander", "/merci", "/confirmation", "/produit"].some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    api.get("/settings/delivery").then(({ data }) => { if (data?.whatsapp_number) setWhatsapp(data.whatsapp_number); }).catch(() => {});
  }, []);

  const waHref = `https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent("Bonjour, je viens de la boutique Noumabou, je souhaite commander.")}`;
  const linkCls = "text-white/80 hover:text-white transition-colors";

  return (
    <>
    <header className="sticky top-0 z-50 text-white shadow-md" style={{ background: "#5B2A9D" }}>
      <div className="w-full max-w-md mx-auto sm:max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="shop-logo" className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <img src="/noumabou-emblem.png" alt="Noumabou" className="w-6 h-6 object-contain" />
            </span>
            <span className="font-heading font-black text-lg sm:text-xl tracking-[0.12em]">
              NOUMABOU{isMinistry && <span className="font-semibold text-white/85"> MINISTRY</span>}
            </span>
          </Link>

          <nav className="hidden sm:flex items-center gap-8 font-medium">
            <Link to="/" className={linkCls} data-testid="nav-home">Accueil</Link>
            <Link to="/ministere" className={linkCls} data-testid="nav-ministere">Ministère</Link>
            <Link to="/boutique" className={linkCls} data-testid="nav-boutique">Boutique</Link>
            <Link to="/login" className={linkCls} data-testid="nav-login">
              {isStaff ? "Gestion" : "Espace pro"}
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="cart-button"
              onClick={openCart}
              className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <ShoppingBag className="w-6 h-6 text-white" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 text-brand-ink text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold" style={{ background: "#C9A227" }}>
                  {count}
                </span>
              )}
            </button>
            <button className="sm:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="sm:hidden py-3 flex flex-col gap-3 border-t border-white/15 font-medium">
            <Link to="/" className={linkCls} onClick={() => setOpen(false)}>Accueil</Link>
            <Link to="/ministere" className={linkCls} onClick={() => setOpen(false)}>Ministère</Link>
            <Link to="/boutique" className={linkCls} onClick={() => setOpen(false)}>Boutique</Link>
            <Link to="/login" className={linkCls} onClick={() => setOpen(false)}>{isStaff ? "Gestion" : "Espace pro"}</Link>
          </div>
        )}
      </div>
    </header>
    {isShop && (
    <a
      href={waHref}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-fab"
      aria-label="Commander sur WhatsApp"
      className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-transform px-4 py-3 font-semibold"
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zM6.597 20.03c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.599 5.393l-.999 3.648 3.89-1.34zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.767.967-.94 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
      <span className="hidden sm:inline text-sm">Commander sur WhatsApp</span>
    </a>
    )}
    <CartDrawer />
    </>
  );
}
